// PNG dışa aktarma — bir kartı "ekran görüntüsü" olarak indirir (Haftalık Rapor tabloları).
// ─────────────────────────────────────────────────────────────────────────────
// Neden kütüphane yok (html2canvas / html-to-image): ikisi de DOM'u bir SVG foreignObject
// içine klonlar; o belge sayfanın web fontuna (Inter, Google Fonts) erişemez → font CSS'ini
// + woff2'leri çekip data URL olarak gömmek gerekir (~1 MB / dışa aktarma, çapraz-origin CSS
// kuralları, Safari foreignObject hataları). Canvas 2D ise BELGENİN yüklü fontunu doğrudan
// kullanır: sıfır ağ isteği, anında sonuç, yeni bağımlılık yok.
//
// Yaklaşım: tarayıcı zaten HER hücreyi yerleştirmiş durumda — yatay/dikey kaydırma yalnız
// GÖRÜNÜMÜ kırpar, geometri tamdır. Kırpan kapsayıcı (`data-png-unclip`) geçici olarak açılır,
// kart taşan içeriği sığdıracak genişliğe getirilir, her elemanın getBoundingClientRect'i +
// getComputedStyle'ı okunup canvas'a çizilir (zemin, kenarlık, metin satırları) ve stiller AYNI
// görevde (senkron, await yok) geri alınır → ekranda tek kare bile değişmez. Metin satırları
// Range.getClientRects ile tarayıcının KENDİ satır kırılımından alınır (başlık hücreleri 10px'te
// birden çok satıra sarar) — yeniden sarma tahmini yok. `data-png-only` işaretli eleman ekranda
// gizlidir (display:none), yalnız dışa aktarmada açılır: başlık şeridi DOM'da durduğu için EN
// kaplaması (i18n.ts) onu da çevirir — canvas'a elle yazılan metin çevrilemezdi.
//
// Sınırlar (bilinçli): z-index/konumlandırma yok (kart akış düzeninde), gradient/gölge/kesikli
// kenarlık yok, <button>/<svg>/<img> ve ikon fontu ATLANIR (ekran görüntüsünde buton anlamsız),
// tabular rakam canvas'ta yok (sağa yaslı sütunlar yine sağdan hizalı; rakam genişliği birkaç px
// oynar), `text-overflow: ellipsis` canvas'ta ölçülerek yeniden üretilir.

/** Retina keskinliği: CSS px başına canvas px. */
const SCALE = 2;
/** Kart çevresindeki sayfa zemini (px, CSS). */
const MARGIN = 20;
/** Çizilmeyen (alt ağacıyla birlikte atlanan) elemanlar. */
const SKIP_TAGS = new Set(['BUTTON', 'SVG', 'IMG', 'CANVAS', 'VIDEO', 'INPUT', 'SELECT', 'TEXTAREA', 'SCRIPT', 'STYLE']);

interface StylePatch {
  el: HTMLElement;
  style: string | null;
}

interface LineBox {
  text: string;
  left: number;
  right: number;
  top: number;
  bottom: number;
}

/** Inline stili yamalar; `restore` özniteliği BİREBİR geri yazar (React kendi diff'ini
 *  prop'lara karşı yapar, DOM'a bakmaz — geçici yama bir sonraki render'ı bozmaz). */
function patch(list: StylePatch[], el: HTMLElement, css: string): void {
  list.push({ el, style: el.getAttribute('style') });
  el.style.cssText = (el.style.cssText ? el.style.cssText + ';' : '') + css;
}

function restore(list: StylePatch[]): void {
  for (let i = list.length - 1; i >= 0; i--) {
    const { el, style } = list[i];
    if (style == null) el.removeAttribute('style');
    else el.setAttribute('style', style);
  }
}

function isTransparent(color: string): boolean {
  return !color || color === 'transparent' || /^rgba\(\s*\d+,\s*\d+,\s*\d+,\s*0\s*\)$/.test(color);
}

/** `border-radius` değeri px ya da % olabilir (dotStyle 50%). */
function radiusOf(raw: string, w: number, h: number): number {
  const v = parseFloat(raw) || 0;
  return raw.endsWith('%') ? (v / 100) * Math.min(w, h) : v;
}

/** Köşeleri yuvarlatılmış dikdörtgen yolu (canvas roundRect'in tarayıcı desteğine bağlı kalmadan). */
function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  if (!rr) {
    ctx.rect(x, y, w, h);
    return;
  }
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function drawBorders(ctx: CanvasRenderingContext2D, cs: CSSStyleDeclaration, x: number, y: number, w: number, h: number, r: number): void {
  const side = (name: 'Top' | 'Right' | 'Bottom' | 'Left') => ({
    w: cs.getPropertyValue('border-' + name.toLowerCase() + '-style') === 'none' ? 0 : parseFloat(cs.getPropertyValue('border-' + name.toLowerCase() + '-width')) || 0,
    c: cs.getPropertyValue('border-' + name.toLowerCase() + '-color'),
  });
  const t = side('Top');
  const rt = side('Right');
  const b = side('Bottom');
  const l = side('Left');
  if (t.w && t.w === rt.w && t.w === b.w && t.w === l.w && t.c === b.c) {
    // Dört kenar eşit (kart, rozet): yarım kalınlık içeri alınmış tek yuvarlak çerçeve.
    ctx.lineWidth = t.w;
    ctx.strokeStyle = t.c;
    roundRectPath(ctx, x + t.w / 2, y + t.w / 2, w - t.w, h - t.w, r - t.w / 2);
    ctx.stroke();
    return;
  }
  // Tek kenarlı (satır alt çizgileri): şerit dolgusu.
  if (t.w) { ctx.fillStyle = t.c; ctx.fillRect(x, y, w, t.w); }
  if (b.w) { ctx.fillStyle = b.c; ctx.fillRect(x, y + h - b.w, w, b.w); }
  if (l.w) { ctx.fillStyle = l.c; ctx.fillRect(x, y, l.w, h); }
  if (rt.w) { ctx.fillStyle = rt.c; ctx.fillRect(x + w - rt.w, y, rt.w, h); }
}

/**
 * Bir metin düğümünün satır kutuları — tarayıcının KENDİ kırılımı. Karakter karakter Range
 * dikdörtgeni okunur, aynı `top`'a düşenler bir satırdır. Yerleşim temiz olduğu için (arada
 * DOM değişmez) her çağrı ucuzdur. Satır sonundaki katlanmış boşluk dikdörtgensiz gelir → düşer.
 */
function lineBoxes(node: Text): LineBox[] {
  const s = node.data;
  const range = document.createRange();
  const lines: LineBox[] = [];
  let cur: LineBox | null = null;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    const space = /\s/.test(ch);
    range.setStart(node, i);
    range.setEnd(node, i + 1);
    const rects = range.getClientRects();
    if (!rects.length) continue;
    const r = rects[0];
    if (!r.width && !r.height) continue;
    if (!cur || Math.abs(r.top - cur.top) > 1) {
      if (space) continue; // satır başı boşluğu satırı BAŞLATMAZ (yoksa sol çapa bir boşluk sola kayar)
      cur = { text: '', left: r.left, right: r.right, top: r.top, bottom: r.bottom };
      lines.push(cur);
    }
    cur.text += ch;
    if (space) continue; // sağ/alt kenar yalnız görünür karakterlerden (sağa yaslı sayı sondaki boşluğa kaymasın)
    if (r.left < cur.left) cur.left = r.left;
    if (r.right > cur.right) cur.right = r.right;
    if (r.bottom > cur.bottom) cur.bottom = r.bottom;
  }
  range.detach();
  return lines.map((l) => ({ ...l, text: l.text.replace(/\s+/g, ' ').trim() })).filter((l) => l.text);
}

/** CSS text-transform — dile duyarlı (TR 'i' → 'İ'; EN başlık satırı `lang="en"` taşır). */
function transformText(text: string, cs: CSSStyleDeclaration, lang: string): string {
  const t = cs.textTransform;
  if (t === 'uppercase') return text.toLocaleUpperCase(lang);
  if (t === 'lowercase') return text.toLocaleLowerCase(lang);
  return text;
}

/** `text-overflow: ellipsis` — metin kutuya sığmıyorsa canvas ölçümüyle kırp + '…'. */
function ellipsize(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (maxWidth <= 0 || ctx.measureText(text).width <= maxWidth) return text;
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(text.slice(0, mid) + '…').width <= maxWidth) lo = mid;
    else hi = mid - 1;
  }
  return lo ? text.slice(0, lo).trimEnd() + '…' : '…';
}

function drawTextNode(ctx: CanvasRenderingContext2D, node: Text, parent: HTMLElement, cs: CSSStyleDeclaration, ox: number, oy: number): void {
  if (!node.data.trim()) return;
  const lines = lineBoxes(node);
  if (!lines.length) return;

  const langEl = parent.closest('[lang]') as HTMLElement | null;
  const lang = (langEl && langEl.lang) || document.documentElement.lang || 'tr';
  ctx.font = [cs.fontStyle, cs.fontWeight, cs.fontSize, cs.fontFamily].join(' ');
  ctx.fillStyle = cs.color;
  if ('letterSpacing' in ctx) (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = cs.letterSpacing === 'normal' ? '0px' : cs.letterSpacing;

  // Taban çizgisi: fontun içerik alanını (ascent+descent) satır kutusuna ortala. Range
  // dikdörtgeni zaten içerik alanı yüksekliğindedir → taban = top + ascent. Metrik yoksa ortala.
  const m = ctx.measureText('Hg');
  const asc = m.fontBoundingBoxAscent;
  const desc = m.fontBoundingBoxDescent;
  const hasMetrics = Number.isFinite(asc) && Number.isFinite(desc) && asc > 0;
  ctx.textBaseline = hasMetrics ? 'alphabetic' : 'middle';

  const clipsText = cs.textOverflow === 'ellipsis' && cs.overflowX !== 'visible' && cs.whiteSpace === 'nowrap';
  const avail = clipsText ? parent.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0) : 0;
  const align = cs.textAlign;

  lines.forEach((l) => {
    let text = transformText(l.text, cs, lang);
    if (clipsText) text = ellipsize(ctx, text, avail);
    const h = l.bottom - l.top;
    const y = (hasMetrics ? l.top + (h - (asc + desc)) / 2 + asc : (l.top + l.bottom) / 2) - oy;
    if (align === 'right' || align === 'end') {
      ctx.textAlign = 'right';
      ctx.fillText(text, l.right - ox, y);
    } else if (align === 'center') {
      ctx.textAlign = 'center';
      ctx.fillText(text, (l.left + l.right) / 2 - ox, y);
    } else {
      ctx.textAlign = 'left';
      ctx.fillText(text, l.left - ox, y);
    }
  });
}

/** Elemanı ve alt ağacını belge sırasıyla çizer: zemin → kenarlık → (kırpma) → metin/çocuklar. */
function paintElement(ctx: CanvasRenderingContext2D, el: Element, ox: number, oy: number): void {
  if (!(el instanceof HTMLElement)) return; // SVG/MathML vb.
  if (SKIP_TAGS.has(el.tagName) || el.hasAttribute('data-png-skip')) return;
  const cs = getComputedStyle(el);
  if (cs.display === 'none' || cs.visibility === 'hidden') return;
  const opacity = parseFloat(cs.opacity);
  if (opacity === 0) return;
  if (/material symbols/i.test(cs.fontFamily)) return; // ikon ligature'ı düz metin olarak basılmasın

  const r = el.getBoundingClientRect();
  if (!r.width && !r.height) return;
  const x = r.left - ox;
  const y = r.top - oy;
  const radius = radiusOf(cs.borderTopLeftRadius, r.width, r.height);

  ctx.save();
  if (opacity < 1) ctx.globalAlpha *= opacity;

  if (!isTransparent(cs.backgroundColor)) {
    roundRectPath(ctx, x, y, r.width, r.height, radius);
    ctx.fillStyle = cs.backgroundColor;
    ctx.fill();
  }
  drawBorders(ctx, cs, x, y, r.width, r.height, radius);

  // overflow ≠ visible → alt ağaç bu kutuya kırpılır (kart köşeleri, ellipsis etiketleri).
  // `data-png-unclip` kapsayıcı yamayla visible'a çekildiği için buraya takılmaz.
  if (cs.overflowX !== 'visible' || cs.overflowY !== 'visible') {
    roundRectPath(ctx, x, y, r.width, r.height, radius);
    ctx.clip();
  }

  el.childNodes.forEach((n) => {
    if (n.nodeType === Node.TEXT_NODE) drawTextNode(ctx, n as Text, el, cs, ox, oy);
    else if (n.nodeType === Node.ELEMENT_NODE) paintElement(ctx, n as Element, ox, oy);
  });
  ctx.restore();
}

/**
 * Kartı canvas'a çizer. Senkron: yama → ölç → çiz → geri al tek görevde biter, tarayıcı arada
 * boyamaz. Ölçüm HER ZAMAN `finally` ile geri alınır — yarım kalan bir çizim ekranı bozamaz.
 */
function renderToCanvas(root: HTMLElement): HTMLCanvasElement {
  const patches: StylePatch[] = [];
  const scrollers: { el: HTMLElement; left: number; top: number }[] = [];
  try {
    // Kaydırma sıfırlanmadan kırpılan bölge ölçülemez; geri yazılır.
    let extraW = 0;
    root.querySelectorAll<HTMLElement>('[data-png-unclip]').forEach((el) => {
      scrollers.push({ el, left: el.scrollLeft, top: el.scrollTop });
      el.scrollLeft = 0;
      el.scrollTop = 0;
      extraW = Math.max(extraW, el.scrollWidth - el.clientWidth);
      patch(patches, el, 'overflow:visible !important;max-height:none !important');
    });
    root.querySelectorAll<HTMLElement>('[data-png-only]').forEach((el) => patch(patches, el, 'display:block !important'));
    // Geçici büyüme sayfa kaydırıcısını "çapa"lamasın (net sıfır olsa da).
    patch(patches, document.documentElement, 'overflow-anchor:none');
    if (extraW > 0) {
      // box-sizing: border-box (index.html `*`) → width kenarlık dahil dış genişliktir.
      patch(patches, root, 'width:' + Math.ceil(root.getBoundingClientRect().width + extraW) + 'px !important;max-width:none !important');
    }

    const rr = root.getBoundingClientRect();
    const w = Math.ceil(rr.width) + MARGIN * 2;
    const h = Math.ceil(rr.height) + MARGIN * 2;
    const canvas = document.createElement('canvas');
    canvas.width = w * SCALE;
    canvas.height = h * SCALE;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d desteklenmiyor');
    ctx.scale(SCALE, SCALE);
    ctx.fillStyle = getComputedStyle(document.body).backgroundColor || '#0d1117';
    ctx.fillRect(0, 0, w, h);
    paintElement(ctx, root, rr.left - MARGIN, rr.top - MARGIN);
    return canvas;
  } finally {
    restore(patches);
    scrollers.forEach((s) => {
      s.el.scrollLeft = s.left;
      s.el.scrollTop = s.top;
    });
  }
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('PNG üretilemedi (canvas çok büyük olabilir)'))), 'image/png');
  });
}

/**
 * `root` kartını PNG olarak indirir. Kırpan kapsayıcılar `data-png-unclip`, yalnız dışa
 * aktarmada görünecek başlık şeridi `data-png-only`, atlanacak parçalar `data-png-skip`
 * (butonlar zaten atlanır). Font yüklenmeden çizmesin diye `document.fonts.ready` beklenir.
 */
export async function downloadPNG(root: HTMLElement, filename: string): Promise<void> {
  if (document.fonts && document.fonts.ready) await document.fonts.ready;
  const canvas = renderToCanvas(root);
  const blob = await canvasToBlob(canvas);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
